// Media Loader Component
// A reusable loading indicator for media uploads with progress tracking

export class MediaLoader {
  constructor(container, options = {}) {
    this.container = container
    this.options = {
      width: options.width || 300,
      height: options.height || 200,
      showProgress: options.showProgress !== false,
      showFilename: options.showFilename !== false,
      ...options
    }
    
    this.currentLoadingElement = null
    this.progressInterval = null
  }

  // Show loading indicator
  show(fileName = 'file') {
    
    // Remove any existing loading indicator
    this.hide()
    
    const loadingElement = document.createElement('div')
    loadingElement.className = 'media-upload-loading'
    loadingElement.innerHTML = this.generateLoadingHTML(fileName)
    
    
    // Insert the loading indicator into the container
    if (this.container) {
      // For Summernote editor, we need to insert into the content area
      if (this.container.classList.contains('summernote')) {
        try {
          // Insert into Summernote editor content
          const $editor = $(this.container)
          if ($editor.length && typeof $editor.summernote === 'function') {
            $editor.summernote('insertNode', loadingElement)
          } else {
            // Fallback: append to container
            this.container.appendChild(loadingElement)
          }
        } catch (error) {
          // Fallback: append to container
          this.container.appendChild(loadingElement)
        }
      } else {
        // Regular container append
        this.container.appendChild(loadingElement)
      }
    }
    
    // Store reference for removal
    this.currentLoadingElement = loadingElement
    
    return loadingElement
  }

  // Hide loading indicator
  hide() {
    if (this.currentLoadingElement) {
      try {
        this.currentLoadingElement.remove()
        this.currentLoadingElement = null
      } catch (error) {
        
      }
    }
    
    // Also remove any stray loading indicators
    if (this.container) {
      // For Summernote editor, remove from editor content
      if (this.container.classList.contains('summernote')) {
        try {
          const $editor = $(this.container)
          if ($editor.length && typeof $editor.summernote === 'function') {
            // Remove only the loading elements from the editor content
            const currentCode = $editor.summernote('code')
            const cleanedCode = currentCode.replace(/<div[^>]*class="[^"]*media-upload-loading[^"]*"[^>]*>[\s\S]*?<\/div>/g, '')
            $editor.summernote('code', cleanedCode)
          }
        } catch (error) {
          // Fallback: just remove the element
          if (this.currentLoadingElement) {
            this.currentLoadingElement.remove()
          }
        }
      } else {
        // Regular container cleanup
        this.container.querySelectorAll('.media-upload-loading').forEach(el => el.remove())
      }
    }
  }

  // Update loading progress
  updateProgress(percentage) {
    if (this.currentLoadingElement) {
      // Update progress text
      const progressElement = this.currentLoadingElement.querySelector('.upload-progress')
      if (progressElement) {
        progressElement.textContent = `Uploading... ${percentage}%`
      }
      
      // Update progress bar
      const progressFill = this.currentLoadingElement.querySelector('.progress-fill')
      if (progressFill) {
        progressFill.style.width = `${percentage}%`
      }
      
      // Update percentage text
      const percentageElement = this.currentLoadingElement.querySelector('.progress-percentage')
      if (percentageElement) {
        percentageElement.textContent = `${percentage}%`
      }
    }
  }

  // Generate loading HTML
  generateLoadingHTML(fileName) {
    const progressBarHTML = this.options.showProgress ? `
      <div class="progress-bar-container">
        <div class="progress-bar">
          <div class="progress-fill" style="width: 0%"></div>
        </div>
        <div class="progress-percentage">0%</div>
      </div>
    ` : ''

    const filenameHTML = this.options.showFilename ? `
      <div class="upload-filename">${fileName}</div>
    ` : ''

    return `
      <div class="upload-placeholder" style="width: ${this.options.width}px; height: ${this.options.height}px;">
        <div class="upload-spinner">
          <div class="spinner"></div>
        </div>
        <div class="upload-text">
          ${filenameHTML}
          <div class="upload-progress">Preparing upload...</div>
          ${progressBarHTML}
        </div>
      </div>
    `
  }

  // Simulate upload progress (for testing/demo purposes)
  simulateProgress(duration = 3000) {
    if (!this.options.showProgress) return
    
    let progress = 0
    const interval = setInterval(() => {
      progress += Math.random() * 15 + 5
      if (progress >= 100) {
        progress = 100
        clearInterval(interval)
      }
      this.updateProgress(Math.round(progress))
    }, duration / 20)
    
    this.progressInterval = interval
    return interval
  }

  // Clear progress simulation
  clearProgressSimulation() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
      this.progressInterval = null
    }
  }

  // Replace loading indicator with content
  replaceWith(content) {
    if (this.currentLoadingElement) {
      const contentElement = typeof content === 'string' ? 
        document.createTextNode(content) : content
      
      this.currentLoadingElement.replaceWith(contentElement)
      this.currentLoadingElement = null
      return true
    }
    return false
  }

  // Replace loading indicator with HTML
  replaceWithHTML(html) {
    if (this.currentLoadingElement) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      const contentElement = tempDiv.firstElementChild || tempDiv.firstChild
      
      this.currentLoadingElement.replaceWith(contentElement)
      this.currentLoadingElement = null
      return true
    }
    return false
  }

  // Check if loading indicator is visible
  isVisible() {
    return this.currentLoadingElement !== null
  }

  // Get current progress percentage
  getCurrentProgress() {
    if (this.currentLoadingElement) {
      const percentageElement = this.currentLoadingElement.querySelector('.progress-percentage')
      if (percentageElement) {
        return parseInt(percentageElement.textContent) || 0
      }
    }
    return 0
  }

  // Destroy the loader instance
  destroy() {
    this.hide()
    this.clearProgressSimulation()
    this.container = null
  }
}

// Default export
export default MediaLoader

// Factory function for easy creation
export function createMediaLoader(container, options = {}) {
  return new MediaLoader(container, options)
}

// Pre-configured loaders for common use cases
export const MediaLoaderPresets = {
  // Simple loader without progress
  simple: (container) => new MediaLoader(container, { 
    showProgress: false, 
    showFilename: false 
  }),
  
  // Compact loader for small spaces
  compact: (container) => new MediaLoader(container, { 
    width: 200, 
    height: 120 
  }),
  
  // Large loader for prominent displays
  large: (container) => new MediaLoader(container, { 
    width: 400, 
    height: 250 
  }),
  
  // Minimal loader with just spinner
  minimal: (container) => new MediaLoader(container, { 
    showProgress: false, 
    showFilename: false,
    width: 100,
    height: 100
  })
}
